package com.partejar.app.feature.gestantes.presentation.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.keyframes
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.luminance
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.semantics.ProgressBarRangeInfo
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.hideFromAccessibility
import androidx.compose.ui.semantics.progressBarRangeInfo
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.stateDescription
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.partejar.app.core.format.formatBrazilianDate
import com.partejar.app.core.ui.components.PartejarStatusBadge
import com.partejar.app.core.ui.components.PartejarStatusBadgeTone
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.PartejarIcons
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import partejar_app.shared.generated.resources.Res
import partejar_app.shared.generated.resources.baby_hand_stage_1_inner_life
import partejar_app.shared.generated.resources.baby_hand_stage_2_inner_life
import partejar_app.shared.generated.resources.baby_hand_stage_3_inner_life
import partejar_app.shared.generated.resources.gestacional_hero_background
import partejar_app.shared.generated.resources.gestacional_hero_background_2_dark
import partejar_app.shared.generated.resources.gestacional_hero_background_3_dark
import partejar_app.shared.generated.resources.gestante_stage_1_body
import partejar_app.shared.generated.resources.gestante_stage_2_body
import partejar_app.shared.generated.resources.gestante_stage_3_body
import kotlinx.coroutines.delay
import kotlin.math.roundToInt

private const val FullTermWeeks = 40
private const val DaysPerWeek = 7
private const val HeroAspectRatio = 941f / 1672f
private const val BreathingDurationMillis = 7_400
private const val LifePulseDurationMillis = 4_600
private const val BodyEntranceDurationMillis = 450
private const val InnerLifeEntranceDelayMillis = 200
private const val InnerLifeEntranceDurationMillis = 420
private const val RingEntranceDelayMillis = 350L
private const val RingEntranceDurationMillis = 1_650
private const val BotanicalGlowPrimaryDurationMillis = 11_600
private const val BotanicalGlowSecondaryDurationMillis = 13_400
private val AbdomenTransformOrigin = TransformOrigin(pivotFractionX = 0.70f, pivotFractionY = 0.66f)

/** Bounds in the screen coordinate space, used only to derive the local contextual header. */
internal data class GestationalHeroAnchor(
    val top: Float,
    val bottom: Float
) {
    val height: Float get() = bottom - top

    fun offsetBy(verticalOffset: Float): GestationalHeroAnchor =
        copy(top = top + verticalOffset, bottom = bottom + verticalOffset)
}

internal enum class GestationalVisualStage {
    STAGE_1,
    STAGE_2,
    STAGE_3
}

internal fun gestationalVisualStage(weeks: Int?, days: Int?): GestationalVisualStage? {
    if (!isValidGestationalAge(weeks, days)) return null
    val validWeeks = weeks ?: return null

    return when (validWeeks) {
        in 0..31 -> GestationalVisualStage.STAGE_1
        in 32..36 -> GestationalVisualStage.STAGE_2
        else -> GestationalVisualStage.STAGE_3
    }
}

internal fun gestationalProgress(weeks: Int?, days: Int?): Float? {
    if (!isValidGestationalAge(weeks, days)) return null
    val validWeeks = weeks ?: return null
    val validDays = days ?: return null

    return ((validWeeks * DaysPerWeek + validDays).toFloat() / (FullTermWeeks * DaysPerWeek))
        .coerceIn(0f, 1f)
}

internal fun gestationalAgeDescription(weeks: Int?, days: Int?): String? =
    if (isValidGestationalAge(weeks, days)) "$weeks semanas e $days dias" else null

internal fun gestationalCompactSubtitle(
    babyName: String?,
    weeks: Int?,
    days: Int?
): String? {
    val baby = babyName?.trim()?.takeIf { it.isNotEmpty() }?.let { "Bebê $it" }
    val age = if (isValidGestationalAge(weeks, days)) {
        buildString {
            append("${weeks}s")
            if (days != 0) append(" ${days}d")
        }
    } else {
        null
    }

    return listOfNotNull(baby, age).joinToString(" · ").ifBlank { null }
}

private fun isValidGestationalAge(weeks: Int?, days: Int?): Boolean =
    weeks != null && weeks >= 0 && days != null && days in 0 until DaysPerWeek

@Composable
internal fun GestationalHero(
    name: String,
    babyName: String?,
    weeks: Int?,
    days: Int?,
    dueDate: String?,
    active: Boolean,
    modifier: Modifier = Modifier,
    showStatusBadge: Boolean = true,
    identityAlpha: State<Float>? = null,
    ringHandoffProgress: State<Float>? = null,
    onIdentityPositioned: (GestationalHeroAnchor) -> Unit = {},
    onProgressRingPositioned: (GestationalHeroAnchor) -> Unit = {},
    onProgressContentPositioned: (Rect) -> Unit = {},
    animationsEnabled: Boolean = true,
    visualSlot: @Composable BoxScope.() -> Unit = {}
) {
    val stage = gestationalVisualStage(weeks, days)
    val ageDescription = gestationalAgeDescription(weeks, days)
    val progress = gestationalProgress(weeks, days)
    val dueDateLabel = formatBrazilianDate(dueDate)
    val background = MaterialTheme.colorScheme.background

    Box(
        modifier = modifier
            .fillMaxWidth()
            .background(background)
            .aspectRatio(HeroAspectRatio)
    ) {
        stage?.let { selectedStage ->
            GestationalHeroArtwork(selectedStage, animationsEnabled = animationsEnabled)
        } ?: Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.radialGradient(
                        colors = listOf(
                            MaterialTheme.colorScheme.tertiary.copy(alpha = 0.10f),
                            MaterialTheme.colorScheme.primary.copy(alpha = 0.04f),
                            background
                        )
                    )
                )
        )

        if (stage != null) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(
                        Brush.horizontalGradient(
                            colors = listOf(
                                Color.Black.copy(alpha = 0.30f),
                                Color.Black.copy(alpha = 0.08f),
                                Color.Transparent
                            )
                        )
                    )
            )
        }
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.verticalGradient(
                        colorStops = if (background.luminance() < 0.5f) arrayOf(
                            0f to Color.Transparent,
                            0.42f to Color.Transparent,
                            0.56f to background.copy(alpha = 0.07f),
                            0.68f to background.copy(alpha = 0.20f),
                            0.79f to background.copy(alpha = 0.43f),
                            0.88f to background.copy(alpha = 0.68f),
                            0.95f to background.copy(alpha = 0.90f),
                            0.985f to background.copy(alpha = 0.98f),
                            1f to background
                        ) else arrayOf(
                            0f to Color.Transparent,
                            0.78f to Color.Transparent,
                            0.86f to background.copy(alpha = 0.06f),
                            0.92f to background.copy(alpha = 0.24f),
                            0.96f to background.copy(alpha = 0.58f),
                            0.985f to background.copy(alpha = 0.88f),
                            1f to background
                        )
                    )
                )
        )

        HeroContent(
            name = name,
            babyName = babyName,
            ageDescription = ageDescription,
            weeks = weeks,
            days = days,
            progress = progress,
            dueDateLabel = dueDateLabel,
            active = active,
            showStatusBadge = showStatusBadge,
            identityAlpha = identityAlpha,
            ringHandoffProgress = ringHandoffProgress,
            animationsEnabled = animationsEnabled,
            onIdentityPositioned = onIdentityPositioned,
            onProgressRingPositioned = onProgressRingPositioned,
            onProgressContentPositioned = onProgressContentPositioned,
            visualSlot = visualSlot
        )
    }
}

@Composable
private fun GestationalHeroArtwork(
    stage: GestationalVisualStage,
    animationsEnabled: Boolean = true
) {
    val resources = stage.artworkResources()
    var entered by remember { mutableStateOf(!animationsEnabled) }
    LaunchedEffect(animationsEnabled) { entered = true }
    val bodyEntryProgress by animateFloatAsState(
        targetValue = if (entered) 1f else 0f,
        animationSpec = tween(
            durationMillis = if (animationsEnabled) BodyEntranceDurationMillis else 0,
            easing = FastOutSlowInEasing
        ),
        label = "gestationalBodyEntrance"
    )
    val innerLifeEntryProgress by animateFloatAsState(
        targetValue = if (entered) 1f else 0f,
        animationSpec = tween(
            durationMillis = if (animationsEnabled) InnerLifeEntranceDurationMillis else 0,
            delayMillis = if (animationsEnabled) InnerLifeEntranceDelayMillis else 0,
            easing = FastOutSlowInEasing
        ),
        label = "gestationalInnerLifeEntrance"
    )
    val ambientMotion = if (animationsEnabled) rememberGestationalAmbientMotion() else null
    val structuralEntryScale = 0.996f + (0.004f * bodyEntryProgress)

    Box(modifier = Modifier.fillMaxSize().clipToBounds()) {
        GestationalArtworkLayer(resources.background)
        if (animationsEnabled) GestationalBotanicalAmbient()
        Box(
            modifier = Modifier
                .fillMaxSize()
                .graphicsLayer {
                    transformOrigin = AbdomenTransformOrigin
                    alpha = bodyEntryProgress
                    scaleX = (ambientMotion?.breathingScaleX?.value ?: 1f) * structuralEntryScale
                    scaleY = (ambientMotion?.breathingScaleY?.value ?: 1f) * structuralEntryScale
                }
        ) {
            GestationalArtworkLayer(resources.body)
            GestationalArtworkLayer(
                resource = resources.innerLife,
                modifier = Modifier.graphicsLayer {
                    alpha = (ambientMotion?.innerLifeAlpha?.value ?: 1f) * innerLifeEntryProgress
                }
            )
        }
    }
}

private data class GestationalBotanicalMotion(
    val primaryAlpha: State<Float>,
    val secondaryAlpha: State<Float>,
    val drift: State<Float>
)

@Composable
private fun GestationalBotanicalAmbient() {
    val transition = rememberInfiniteTransition(label = "gestationalBotanicalAmbient")
    val primaryAlpha = transition.animateFloat(
        initialValue = 0.12f,
        targetValue = 0.12f,
        animationSpec = infiniteRepeatable(
            animation = keyframes {
                durationMillis = BotanicalGlowPrimaryDurationMillis
                0.12f at 0 using FastOutSlowInEasing
                0.28f at 3_700 using FastOutSlowInEasing
                0.18f at 7_200 using FastOutSlowInEasing
                0.12f at BotanicalGlowPrimaryDurationMillis
            },
            repeatMode = RepeatMode.Restart
        ),
        label = "gestationalBotanicalPrimaryAlpha"
    )
    val secondaryAlpha = transition.animateFloat(
        initialValue = 0.22f,
        targetValue = 0.22f,
        animationSpec = infiniteRepeatable(
            animation = keyframes {
                durationMillis = BotanicalGlowSecondaryDurationMillis
                0.22f at 0 using FastOutSlowInEasing
                0.13f at 4_100 using FastOutSlowInEasing
                0.26f at 9_300 using FastOutSlowInEasing
                0.22f at BotanicalGlowSecondaryDurationMillis
            },
            repeatMode = RepeatMode.Restart
        ),
        label = "gestationalBotanicalSecondaryAlpha"
    )
    val drift = transition.animateFloat(
        initialValue = 0f,
        targetValue = 0f,
        animationSpec = infiniteRepeatable(
            animation = keyframes {
                durationMillis = BotanicalGlowSecondaryDurationMillis
                0f at 0 using FastOutSlowInEasing
                0.7f at 4_600 using FastOutSlowInEasing
                -0.35f at 9_800 using FastOutSlowInEasing
                0f at BotanicalGlowSecondaryDurationMillis
            },
            repeatMode = RepeatMode.Restart
        ),
        label = "gestationalBotanicalDrift"
    )
    val motion = remember(primaryAlpha, secondaryAlpha, drift) {
        GestationalBotanicalMotion(primaryAlpha, secondaryAlpha, drift)
    }
    val accent = MaterialTheme.colorScheme.tertiary

    Canvas(modifier = Modifier.fillMaxSize()) {
        val driftPx = motion.drift.value.dp.toPx()
        drawBotanicalHighlight(
            center = Offset(size.width * 0.245f + driftPx, size.height * 0.126f),
            color = accent,
            alpha = motion.primaryAlpha.value
        )
        drawBotanicalHighlight(
            center = Offset(size.width * 0.176f - driftPx * 0.6f, size.height * 0.668f),
            color = accent,
            alpha = motion.secondaryAlpha.value * 0.82f
        )
        drawBotanicalHighlight(
            center = Offset(size.width * 0.286f + driftPx * 0.45f, size.height * 0.843f),
            color = accent,
            alpha = motion.primaryAlpha.value * 0.72f
        )
    }
}

private fun androidx.compose.ui.graphics.drawscope.DrawScope.drawBotanicalHighlight(
    center: Offset,
    color: Color,
    alpha: Float
) {
    drawCircle(color = color.copy(alpha = alpha * 0.12f), radius = 8.dp.toPx(), center = center)
    drawCircle(color = color.copy(alpha = alpha * 0.34f), radius = 2.2.dp.toPx(), center = center)
    drawCircle(color = Color.White.copy(alpha = alpha * 0.42f), radius = 0.8.dp.toPx(), center = center)
}

private data class GestationalAmbientMotion(
    val breathingScaleX: State<Float>,
    val breathingScaleY: State<Float>,
    val innerLifeAlpha: State<Float>
)

@Composable
private fun rememberGestationalAmbientMotion(): GestationalAmbientMotion {
    val transition = rememberInfiniteTransition(label = "gestationalHeroLifeMotion")
    val breathingScaleX = transition.animateFloat(
        initialValue = 1f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = keyframes {
                durationMillis = BreathingDurationMillis
                1f at 0 using FastOutSlowInEasing
                1.006f at 2_960 using FastOutSlowInEasing
                1.006f at 3_550 using FastOutSlowInEasing
                1f at BreathingDurationMillis
            },
            repeatMode = RepeatMode.Restart
        ),
        label = "gestationalBodyBreathingScaleX"
    )
    val breathingScaleY = transition.animateFloat(
        initialValue = 1f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = keyframes {
                durationMillis = BreathingDurationMillis
                1f at 0 using FastOutSlowInEasing
                1.013f at 2_960 using FastOutSlowInEasing
                1.013f at 3_550 using FastOutSlowInEasing
                1f at BreathingDurationMillis
            },
            repeatMode = RepeatMode.Restart
        ),
        label = "gestationalBodyBreathingScaleY"
    )
    val lifeAlpha = transition.animateFloat(
        initialValue = 0.88f,
        targetValue = 0.88f,
        animationSpec = infiniteRepeatable(
            animation = keyframes {
                durationMillis = LifePulseDurationMillis
                0.88f at 0 using FastOutSlowInEasing
                1f at 1_650 using FastOutSlowInEasing
                0.96f at 2_200 using FastOutSlowInEasing
                0.88f at LifePulseDurationMillis
            },
            repeatMode = RepeatMode.Restart
        ),
        label = "gestationalInnerLifeAlpha"
    )
    return remember(breathingScaleX, breathingScaleY, lifeAlpha) {
        GestationalAmbientMotion(breathingScaleX, breathingScaleY, lifeAlpha)
    }
}

@Composable
private fun GestationalArtworkLayer(
    resource: DrawableResource,
    modifier: Modifier = Modifier
) {
    Image(
        painter = painterResource(resource),
        contentDescription = null,
        modifier = modifier.fillMaxSize(),
        contentScale = ContentScale.Crop,
        alignment = Alignment.TopCenter
    )
}

internal data class GestationalArtworkResources(
    val background: DrawableResource,
    val body: DrawableResource,
    val innerLife: DrawableResource
)

internal fun GestationalVisualStage.artworkResources() = when (this) {
    GestationalVisualStage.STAGE_1 -> GestationalArtworkResources(
        background = Res.drawable.gestacional_hero_background,
        body = Res.drawable.gestante_stage_1_body,
        innerLife = Res.drawable.baby_hand_stage_1_inner_life
    )
    GestationalVisualStage.STAGE_2 -> GestationalArtworkResources(
        background = Res.drawable.gestacional_hero_background_2_dark,
        body = Res.drawable.gestante_stage_2_body,
        innerLife = Res.drawable.baby_hand_stage_2_inner_life
    )
    GestationalVisualStage.STAGE_3 -> GestationalArtworkResources(
        background = Res.drawable.gestacional_hero_background_3_dark,
        body = Res.drawable.gestante_stage_3_body,
        innerLife = Res.drawable.baby_hand_stage_3_inner_life
    )
}

@Composable
private fun HeroContent(
    name: String,
    babyName: String?,
    ageDescription: String?,
    weeks: Int?,
    days: Int?,
    progress: Float?,
    dueDateLabel: String?,
    active: Boolean,
    showStatusBadge: Boolean,
    identityAlpha: State<Float>?,
    ringHandoffProgress: State<Float>?,
    animationsEnabled: Boolean,
    onIdentityPositioned: (GestationalHeroAnchor) -> Unit,
    onProgressRingPositioned: (GestationalHeroAnchor) -> Unit,
    onProgressContentPositioned: (Rect) -> Unit,
    visualSlot: @Composable BoxScope.() -> Unit
) {
    val spacing = PartejarTheme.spacing

    Column(
        modifier = Modifier
            .fillMaxSize()
            .windowInsetsPadding(
                WindowInsets.safeDrawing.only(
                    WindowInsetsSides.Top + WindowInsetsSides.Horizontal
                )
            )
            .padding(
                start = spacing.space16,
                end = spacing.space16,
                bottom = spacing.space24
            )
    ) {
        Spacer(modifier = Modifier.height(spacing.space64))
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.Top
        ) {
            HeroIdentity(
                name = name,
                babyName = babyName,
                alpha = identityAlpha?.value ?: 1f,
                animationsEnabled = animationsEnabled,
                modifier = Modifier
                    .weight(1f)
                    .onGloballyPositioned { coordinates ->
                        onIdentityPositioned(coordinates.boundsInRoot().toAnchor())
                    }
            )
            if (showStatusBadge) {
                PartejarStatusBadge(
                    text = if (active) "Ativa" else "Encerrada",
                    tone = if (active) PartejarStatusBadgeTone.SUCCESS else PartejarStatusBadgeTone.NEUTRAL
                )
            }
        }

        Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
            GestationalProgressRing(
                ageDescription = ageDescription,
                weeks = weeks,
                days = days,
                progress = progress,
                handoffProgress = ringHandoffProgress,
                animationsEnabled = animationsEnabled,
                modifier = Modifier.align(Alignment.CenterStart),
                onPositioned = onProgressRingPositioned,
                onContentPositioned = onProgressContentPositioned,
                visualSlot = visualSlot
            )
            HeroDueDate(
                dueDateLabel = dueDateLabel,
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .padding(bottom = spacing.space48)
            )
        }
    }
}

@Composable
private fun HeroIdentity(
    name: String,
    babyName: String?,
    alpha: Float,
    animationsEnabled: Boolean,
    modifier: Modifier
) {
    val spacing = PartejarTheme.spacing
    val mediaTitle = Color.White.copy(alpha = 0.94f)
    var entered by remember { mutableStateOf(!animationsEnabled) }
    LaunchedEffect(animationsEnabled) { entered = true }
    val entranceAlpha by animateFloatAsState(
        targetValue = if (entered) 1f else 0f,
        animationSpec = tween(
            durationMillis = if (animationsEnabled) 420 else 0,
            delayMillis = if (animationsEnabled) 100 else 0,
            easing = FastOutSlowInEasing
        ),
        label = "gestationalIdentityEntrance"
    )
    val effectiveAlpha = alpha * entranceAlpha
    Column(
        modifier = modifier
            .then(if (effectiveAlpha < 0.5f) Modifier.semantics { hideFromAccessibility() } else Modifier)
            .graphicsLayer { this.alpha = effectiveAlpha },
        verticalArrangement = Arrangement.spacedBy(spacing.space4)
    ) {
        Text(
            text = name,
            style = MaterialTheme.typography.headlineLarge,
            color = mediaTitle,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )
        babyName?.let {
            Row(
                horizontalArrangement = Arrangement.spacedBy(spacing.space8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Surface(
                    modifier = Modifier.size(32.dp),
                    shape = MaterialTheme.shapes.extraLarge,
                    color = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.14f),
                    contentColor = MaterialTheme.colorScheme.tertiary,
                    border = BorderStroke(
                        PartejarTheme.dimensions.strokeThin,
                        MaterialTheme.colorScheme.tertiary.copy(alpha = 0.46f)
                    )
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(
                            imageVector = PartejarIcons.Navigation.gestantes,
                            contentDescription = null,
                            modifier = Modifier.size(PartejarTheme.dimensions.iconSmall)
                        )
                    }
                }
                Text(
                    text = "Bebê $it",
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.tertiary,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@Composable
private fun HeroDueDate(
    dueDateLabel: String?,
    modifier: Modifier = Modifier
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Surface(
        modifier = modifier,
        shape = MaterialTheme.shapes.extraLarge,
        color = Color.Black.copy(alpha = 0.28f),
        contentColor = Color.White.copy(alpha = 0.94f),
        border = BorderStroke(
            dimensions.strokeThin,
            MaterialTheme.colorScheme.tertiary.copy(alpha = 0.58f)
        )
    ) {
        Row(
            modifier = Modifier.padding(horizontal = spacing.space16, vertical = spacing.space12),
            horizontalArrangement = Arrangement.spacedBy(spacing.space8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                imageVector = PartejarIcons.Clinical.calendar,
                contentDescription = null,
                modifier = Modifier.size(dimensions.iconMedium),
                tint = MaterialTheme.colorScheme.tertiary
            )
            Text(
                text = dueDateLabel?.let { "DPP · $it" } ?: "DPP não informada",
                style = MaterialTheme.typography.labelLarge,
                color = Color.White.copy(alpha = 0.94f)
            )
        }
    }
}

@Composable
private fun GestationalProgressRing(
    ageDescription: String?,
    weeks: Int?,
    days: Int?,
    progress: Float?,
    handoffProgress: State<Float>?,
    animationsEnabled: Boolean,
    modifier: Modifier,
    onPositioned: (GestationalHeroAnchor) -> Unit,
    onContentPositioned: (Rect) -> Unit,
    visualSlot: @Composable BoxScope.() -> Unit
) {
    val dimensions = PartejarTheme.dimensions
    val trackColor = Color.White.copy(alpha = 0.20f)
    val progressColor = MaterialTheme.colorScheme.tertiary
    val mediaTitle = Color.White.copy(alpha = 0.94f)
    val mediaMetadata = Color.White.copy(alpha = 0.72f)

    var entered by remember { mutableStateOf(false) }
    LaunchedEffect(progress, animationsEnabled) {
        if (progress != null && !entered) {
            if (animationsEnabled) delay(RingEntranceDelayMillis)
            entered = true
        }
    }
    val entryProgress by animateFloatAsState(
        targetValue = if (!animationsEnabled && progress != null) 1f else if (entered) 1f else 0f,
        animationSpec = tween(
            durationMillis = if (animationsEnabled) RingEntranceDurationMillis else 0,
            easing = FastOutSlowInEasing
        ),
        label = "gestationalRingEntrance"
    )
    val handoff = handoffProgress?.value ?: 0f
    val sourceExitProgress = (handoff / 0.20f).coerceIn(0f, 1f)
    val ringContentAlpha = 1f - sourceExitProgress
    val ringContentScale = 1f - (0.10f * sourceExitProgress)
    val daysEntryProgress = ((entryProgress - 0.70f) / 0.30f).coerceIn(0f, 1f)
    val animatedProgress = (progress ?: 0f) * entryProgress
    val displayedWeeks = ((weeks ?: 0) * entryProgress).roundToInt()

    Box(
        modifier = modifier
            .size(156.dp)
            .onGloballyPositioned { coordinates ->
                onPositioned(coordinates.boundsInRoot().toAnchor())
            }
            .semantics {
                if (handoff > 0.91f) hideFromAccessibility()
                contentDescription = if (ageDescription == null) {
                    "Idade gestacional indisponível"
                } else {
                    "Idade gestacional: $ageDescription"
                }
                stateDescription = if (progress == null) {
                    "Progresso gestacional indisponível"
                } else {
                    "${(progress * 100).toInt()} por cento de 40 semanas"
                }
                progress?.let { progressBarRangeInfo = ProgressBarRangeInfo(it, 0f..1f) }
            },
        contentAlignment = Alignment.Center
    ) {
        if (progress != null) {
            Canvas(
                modifier = Modifier
                    .fillMaxSize()
                    .graphicsLayer { alpha = 1f - (0.82f * handoff * handoff) }
            ) {
                val stroke = dimensions.strokeStrong.toPx()
                val inset = stroke / 2
                val arcSize = Size(size.width - stroke, size.height - stroke)
                drawArc(
                    color = trackColor,
                    startAngle = -90f,
                    sweepAngle = 330f,
                    useCenter = false,
                    topLeft = Offset(inset, inset),
                    size = arcSize,
                    style = Stroke(width = stroke, cap = StrokeCap.Round)
                )
                drawArc(
                    color = progressColor,
                    startAngle = -90f,
                    sweepAngle = 330f * animatedProgress,
                    useCenter = false,
                    topLeft = Offset(inset, inset),
                    size = arcSize,
                    style = Stroke(width = stroke, cap = StrokeCap.Round)
                )
            }
        }
        visualSlot()
        Box(
            modifier = Modifier.onGloballyPositioned { coordinates ->
                onContentPositioned(coordinates.boundsInRoot())
            }
        ) {
            Column(
                modifier = Modifier.graphicsLayer {
                    alpha = ringContentAlpha
                    scaleX = ringContentScale
                    scaleY = ringContentScale
                },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (ageDescription == null) {
                    Text("IG", style = MaterialTheme.typography.labelLarge, color = mediaMetadata)
                    Text("indisponível", style = MaterialTheme.typography.titleSmall, color = mediaTitle)
                } else {
                    Text(
                        text = displayedWeeks.toString(),
                        modifier = Modifier.graphicsLayer {
                            alpha = 0.18f + (0.82f * entryProgress)
                            val scale = 0.88f + (0.12f * entryProgress)
                            scaleX = scale
                            scaleY = scale
                        },
                        style = MaterialTheme.typography.displayMedium,
                        color = mediaTitle
                    )
                    Text(
                        "semanas",
                        modifier = Modifier.graphicsLayer { alpha = 0.35f + (0.65f * entryProgress) },
                        style = MaterialTheme.typography.titleSmall,
                        color = mediaTitle
                    )
                    Text(
                        text = "$days dias",
                        modifier = Modifier.graphicsLayer { alpha = daysEntryProgress },
                        style = MaterialTheme.typography.bodySmall,
                        color = mediaMetadata
                    )
                }
            }
        }
    }
}

private fun Rect.toAnchor(): GestationalHeroAnchor = GestationalHeroAnchor(
    top = top,
    bottom = bottom
)
